import { Button, Dialog } from '@artosera/ui';
import type { DroitsFins, RoleGalerie } from '@artosera/shared';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDelierIdentite, useIdentitesSso, type IdentiteSso } from '../../api/sso.js';
import {
  capturerPortee,
  exigerActuelle,
  estActuelle,
  estPorteeObsolete,
  type CapturePortee,
} from '../../auth/identite.js';
import { useApresConnexion } from '../../auth/session.js';
import { estErreurStepUpRequis } from '../../api/coffre.js';
import {
  useEquipe,
  useModifierDroitsMembre,
  useModifierRoleMembre,
  type MembreEquipe,
} from '../../api/equipe.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { StepUpDialog } from '../coffre/StepUpDialog.js';
import { useShellContext } from '../shell/context.js';
import { LigneMembre } from './LigneMembre.js';
import styles from './EquipePage.module.css';

type Deliaison = { readonly identite: IdentiteSso; readonly portee: CapturePortee };

type Geste =
  | {
      readonly type: 'role';
      readonly membre: MembreEquipe;
      readonly role: RoleGalerie;
      readonly revision: number;
    }
  | {
      readonly type: 'droits';
      readonly membre: MembreEquipe;
      readonly droits: DroitsFins;
      readonly revision: number;
    };

/**
 * Réglages › Équipe (M7) : qui travaille dans la galerie, son rôle simple et
 * ses droits fins. La direction modifie — jamais sa propre ligne, jamais un
 * accès de support ; le serveur refuse le reste (`session/equipe.ts`).
 */
export function EquipePage(): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie, compte } = useShellContext();
  const equipe = useEquipe(galerieSlug);
  const avecSso = galerieAModule(galerie, 'sso');
  const identites = useIdentitesSso(galerieSlug, avecSso);
  const delier = useDelierIdentite(galerieSlug);
  const navigate = useNavigate();
  const apresConnexion = useApresConnexion();
  const [aDelier, setADelier] = useState<Deliaison | null>(null);
  const [ssoARejouer, setSsoARejouer] = useState<Deliaison | null>(null);
  const modifierRole = useModifierRoleMembre(galerieSlug);
  const modifierDroits = useModifierDroitsMembre(galerieSlug);
  const [aConfirmer, setAConfirmer] = useState<Extract<Geste, { type: 'role' }> | null>(null);
  const [aRejouer, setARejouer] = useState<Geste | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const direction = !galerie.support && galerieALaction(galerie, 'settings:team');
  const droitsModifiables =
    galerieEcritModule(galerie, 'roles-fins') && equipe.data?.droitsAppliques !== false;
  const revision = equipe.data?.revision ?? 0;
  const enCours = modifierRole.isPending || modifierDroits.isPending || delier.isPending;

  function executer(geste: Geste): void {
    setMessage(null);
    const options = {
      onError: (erreur: Error) => {
        if (estErreurStepUpRequis(erreur)) {
          setARejouer(geste);
          return;
        }
        if (
          estApiError(erreur) &&
          (erreur.details as { raison?: string } | null)?.raison === 'revision_equipe_perimee'
        ) {
          setMessage(t('regie:reglages.equipe.revisionPerimee'));
          return;
        }
        setMessage(estApiError(erreur) ? erreur.message : t('commun:erreurReseau'));
      },
    };
    if (geste.type === 'role') {
      modifierRole.mutate(
        { compteId: geste.membre.compteId, role: geste.role, revision: geste.revision },
        options,
      );
    } else {
      modifierDroits.mutate(
        { compteId: geste.membre.compteId, droits: geste.droits, revision: geste.revision },
        options,
      );
    }
  }

  async function executerDeliaison(geste: Deliaison): Promise<void> {
    if (!estActuelle(geste.portee)) return;
    setMessage(null);
    try {
      await delier.mutateAsync(geste.identite.id);
      exigerActuelle(geste.portee);
      if (geste.identite.compteId === compte.id && compte.connexion?.methode === 'sso') {
        const oubli = apresConnexion();
        const apres = capturerPortee();
        await oubli;
        exigerActuelle(apres);
        await navigate(`/connexion/sso?galerie=${encodeURIComponent(galerieSlug)}`, {
          replace: true,
        });
      }
    } catch (erreur) {
      if (estPorteeObsolete(erreur) || !estActuelle(geste.portee)) return;
      if (estErreurStepUpRequis(erreur)) setSsoARejouer(geste);
      else setMessage(estApiError(erreur) ? erreur.message : t('commun:erreurReseau'));
    }
  }

  return (
    <div className={styles.page} data-ui="team">
      <h1 className={styles.titre} data-ui="page-title">
        {t('regie:reglages.equipe.titre')}
      </h1>
      <p className={styles.sousTitre}>{t('regie:reglages.equipe.sousTitre')}</p>
      {direction && !droitsModifiables ? (
        <p className={styles.note} role="note">
          {t('regie:reglages.equipe.moduleCoupe')}
        </p>
      ) : null}
      {avecSso && identites.isError ? (
        <p className={styles.alerte} role="alert">
          {t('regie:reglages.equipe.ssoErreur')}
        </p>
      ) : null}
      {message ? (
        <p className={styles.alerte} role="alert">
          {message}
        </p>
      ) : null}

      {equipe.isLoading ? (
        <p className={styles.vide}>{t('regie:commun.chargement')}</p>
      ) : equipe.isError ? (
        <p className={styles.vide} role="alert">
          {estApiError(equipe.error) ? equipe.error.message : t('commun:erreurReseau')}
        </p>
      ) : (
        <div
          className={styles.cadre}
          data-ui="team-table"
          role="table"
          aria-label={t('regie:reglages.equipe.titre')}
        >
          <div className={styles.entete} data-sso={avecSso} role="row">
            <span role="columnheader">{t('regie:reglages.equipe.colonneNom')}</span>
            <span role="columnheader">{t('regie:reglages.equipe.colonneRole')}</span>
            <span role="columnheader">{t('regie:reglages.equipe.colonneDroits')}</span>
            {avecSso ? (
              <span role="columnheader">{t('regie:reglages.equipe.colonneSso')}</span>
            ) : null}
          </div>
          {equipe.data?.membres.map((membre) => {
            const soi = membre.compteId === compte.id;
            const identite = identites.data?.identites.find((i) => i.compteId === membre.compteId);
            return (
              <LigneMembre
                key={membre.compteId}
                membre={membre}
                soi={soi}
                connexionSso={
                  avecSso ? (
                    <>
                      <span className={styles.labelSso}>
                        {t('regie:reglages.equipe.colonneSso')}
                      </span>
                      <span>
                        {identites.isPending
                          ? t('regie:commun.chargement')
                          : identites.isError
                            ? '—'
                            : t(
                                identite
                                  ? 'regie:reglages.equipe.ssoLiee'
                                  : 'regie:reglages.equipe.ssoNonLiee',
                              )}
                      </span>
                      {identite &&
                      !identites.isError &&
                      direction &&
                      !membre.support &&
                      galerieEcritModule(galerie, 'sso') ? (
                        <Button
                          variant="secondary"
                          disabled={enCours}
                          onClick={() => setADelier({ identite, portee: capturerPortee() })}
                        >
                          {t('regie:reglages.equipe.delier')}
                        </Button>
                      ) : null}
                    </>
                  ) : undefined
                }
                modifiable={direction && !soi && !membre.support}
                droitsModifiables={droitsModifiables}
                enCours={enCours}
                onRole={(role) => setAConfirmer({ type: 'role', membre, role, revision })}
                onDroits={(droits) => executer({ type: 'droits', membre, droits, revision })}
              />
            );
          })}
        </div>
      )}

      <Dialog
        open={aConfirmer !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setAConfirmer(null);
        }}
        title={t('regie:reglages.equipe.confirmerTitre', { nom: aConfirmer?.membre.nom ?? '' })}
        closeLabel={t('regie:reglages.equipe.annuler')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAConfirmer(null)}>
              {t('regie:reglages.equipe.annuler')}
            </Button>
            <Button
              onClick={() => {
                if (aConfirmer) executer(aConfirmer);
                setAConfirmer(null);
              }}
            >
              {t('regie:reglages.equipe.confirmer')}
            </Button>
          </>
        }
      >
        {aConfirmer ? (
          <>
            <p>{t('regie:reglages.equipe.confirmerDroits', { nom: aConfirmer.membre.nom })}</p>
            {aConfirmer.role === 'direction' ? (
              <p>
                {t('regie:reglages.equipe.confirmerVersDirection', { nom: aConfirmer.membre.nom })}
              </p>
            ) : null}
            {aConfirmer.membre.role === 'direction' ? (
              <p>
                {t('regie:reglages.equipe.confirmerDepuisDirection', {
                  nom: aConfirmer.membre.nom,
                })}
              </p>
            ) : null}
          </>
        ) : null}
      </Dialog>

      <Dialog
        open={aDelier !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setADelier(null);
        }}
        title={t('regie:reglages.equipe.delierTitre')}
        closeLabel={t('regie:reglages.equipe.annuler')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setADelier(null)}>
              {t('regie:reglages.equipe.annuler')}
            </Button>
            <Button
              disabled={enCours}
              onClick={() => {
                if (aDelier) void executerDeliaison(aDelier);
                setADelier(null);
              }}
            >
              {t('regie:reglages.equipe.delier')}
            </Button>
          </>
        }
      >
        <p>{t('regie:reglages.equipe.delierTexte')}</p>
      </Dialog>
      <StepUpDialog
        open={ssoARejouer !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setSsoARejouer(null);
        }}
        description={t('regie:reglages.equipe.stepUp')}
        onVerifie={() => {
          if (ssoARejouer) void executerDeliaison(ssoARejouer);
          setSsoARejouer(null);
        }}
      />
      <StepUpDialog
        open={aRejouer !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setARejouer(null);
        }}
        description={t('regie:reglages.equipe.stepUp')}
        onVerifie={() => {
          if (aRejouer) executer(aRejouer);
          setARejouer(null);
        }}
      />
    </div>
  );
}
